/**
 * AssetGrid — status-filtered, paginated view of one owner's assets, with
 * inline tag editing and soft-delete unless read-only. Owner-agnostic via
 * `AssetLibraryAdapter`.
 *
 * Display controls (all server-side, so they hold up at thousands of
 * assets): grid/list view toggle (list skips thumbnails entirely), sort
 * (newest/oldest/name/size), filename substring search, and a single-tag
 * filter. Search and tag input are debounced so a keystroke isn't a
 * request; changing any control resets the keyset cursor to page one —
 * a cursor minted under a different sort/filter is meaningless.
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { AssetLibraryAdapter } from './adapter';
import type { ContributorArcheAsset, AssetListSortValue } from '@/lib/api';
import { AssetCard } from './AssetCard';
import { formatBytes } from './format';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'name_desc', label: 'Name Z→A' },
  { value: 'size_desc', label: 'Largest first' },
  { value: 'size_asc', label: 'Smallest first' },
] as const satisfies readonly { value: AssetListSortValue; label: string }[];

/** Debounce a fast-changing value (search keystrokes → one request). */
function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface AssetGridProps {
  adapter: AssetLibraryAdapter;
  /** Card title. Default "Assets". */
  title?: string;
  /** Hides tag editing + delete (viewer role, disabled or suspended library). */
  readOnly?: boolean;
}

export function AssetGrid({ adapter, title = 'Assets', readOnly = false }: AssetGridProps) {
  const [statusFilter, setStatusFilter] = React.useState<string>('active');
  const [view, setView] = React.useState<'grid' | 'list'>('grid');
  const [sort, setSort] = React.useState<AssetListSortValue>('newest');
  const [search, setSearch] = React.useState('');
  const [tagFilter, setTagFilter] = React.useState('');
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const debouncedSearch = useDebouncedValue(search.trim());
  const debouncedTag = useDebouncedValue(tagFilter.trim());

  // A page cursor only means something under the exact filter/sort that
  // minted it. The sort/status handlers reset it SYNCHRONOUSLY (React
  // batches the paired setState, so no render ever pairs a stale cursor
  // with a new filter — with an effect, a status change on page 2 fired
  // one wasted wrong-page fetch). The debounced values can't reset in a
  // handler, so they keep an effect.
  React.useEffect(() => {
    setCursor(undefined);
  }, [debouncedSearch, debouncedTag]);

  // Destructured so the `use*` names are visible to react-hooks/rules-of-hooks
  // (a `adapter.useX()` member call is invisible to the lint rule).
  const { useAssetsList, useSoftDelete, usePatch } = adapter;
  const listQuery = useAssetsList({
    status: statusFilter,
    cursor,
    sort,
    q: debouncedSearch || undefined,
    tag: debouncedTag || undefined,
  });
  const deleteMutation = useSoftDelete();
  const patchMutation = usePatch();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <div className="inline-flex overflow-hidden rounded border border-input" role="group">
            <Button
              type="button"
              variant={view === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none"
              aria-pressed={view === 'grid'}
              onClick={() => setView('grid')}
            >
              Grid
            </Button>
            <Button
              type="button"
              variant={view === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none"
              aria-pressed={view === 'list'}
              onClick={() => setView('list')}
            >
              List
            </Button>
          </div>
        </CardTitle>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="search"
            className="w-44 rounded border border-input bg-background px-2 py-1 text-sm font-normal"
            placeholder="Search filenames…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search assets by filename"
          />
          <input
            type="text"
            className="w-40 rounded border border-input bg-background px-2 py-1 text-sm font-normal"
            placeholder="Filter by tag…"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            aria-label="Filter assets by tag"
          />
          <select
            className="rounded border border-input bg-background p-1 text-sm font-normal"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as AssetListSortValue);
              setCursor(undefined);
            }}
            aria-label="Sort assets"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-input bg-background p-1 text-sm font-normal"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCursor(undefined);
            }}
            aria-label="Filter by status"
          >
            <option value="active">Active</option>
            <option value="pending_validation">Pending</option>
            <option value="validation_failed">Failed</option>
            <option value="soft_deleted">Deleted</option>
            <option value="all_visible">All visible</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {listQuery.isLoading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : listQuery.isError ? (
          <div className="text-destructive">Could not load assets.</div>
        ) : !listQuery.data || listQuery.data.assets.length === 0 ? (
          <div className="text-muted-foreground">No assets match.</div>
        ) : view === 'list' ? (
          <AssetList
            assets={listQuery.data.assets}
            readOnly={readOnly}
            onDelete={(assetId) => deleteMutation.mutate(assetId)}
            deletingAssetId={deleteMutation.isPending ? deleteMutation.variables : undefined}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {listQuery.data.assets.map((asset) => (
              <AssetCard
                key={asset.assetId}
                asset={asset}
                previewUrl={
                  asset.status === 'active' ? adapter.buildPreviewUrl(asset.assetId) : null
                }
                readOnly={readOnly}
                onDelete={() => deleteMutation.mutate(asset.assetId)}
                deleting={deleteMutation.isPending && deleteMutation.variables === asset.assetId}
                onSaveTags={(tags) => patchMutation.mutateAsync({ assetId: asset.assetId, tags })}
              />
            ))}
          </div>
        )}
        {deleteMutation.isError && (
          <div role="alert" className="mt-4 text-sm text-destructive">
            Could not delete the asset:{' '}
            {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'unknown error'}
          </div>
        )}
        {listQuery.data && (listQuery.data.nextCursor || cursor) && (
          <div className="mt-4 flex justify-center gap-2">
            {cursor && (
              <Button variant="outline" size="sm" onClick={() => setCursor(undefined)}>
                First page
              </Button>
            )}
            {listQuery.data.nextCursor && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCursor(listQuery.data?.nextCursor ?? undefined)}
              >
                Next page
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact no-thumbnail rows for scanning a large library: name, dimensions,
 * size, upload date, tags. Wide content scrolls inside the table's own
 * container — the page never scrolls horizontally.
 */
function AssetList({
  assets,
  readOnly,
  onDelete,
  deletingAssetId,
}: {
  assets: ContributorArcheAsset[];
  readOnly: boolean;
  onDelete: (assetId: string) => void;
  deletingAssetId: string | undefined;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Name</th>
            <th className="py-2 pr-3 font-medium">Dimensions</th>
            <th className="py-2 pr-3 font-medium">Size</th>
            <th className="py-2 pr-3 font-medium">Uploaded</th>
            <th className="py-2 pr-3 font-medium">Tags</th>
            {!readOnly && <th className="py-2 font-medium" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr key={asset.assetId} className="border-b last:border-b-0">
              <td
                className="max-w-[16rem] truncate py-2 pr-3 font-medium"
                title={asset.name ?? asset.assetId}
              >
                {asset.name ?? asset.assetId}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                {asset.widthPx && asset.heightPx ? `${asset.widthPx}×${asset.heightPx}` : '—'}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                {formatBytes(asset.sizeBytes)}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                {asset.uploadedAt ? new Date(asset.uploadedAt).toLocaleDateString() : '—'}
              </td>
              <td
                className="max-w-[20rem] truncate py-2 pr-3 text-muted-foreground"
                title={(asset.tags ?? []).join(', ')}
              >
                {(asset.tags ?? []).join(', ') || '—'}
              </td>
              {!readOnly && (
                <td className="py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(asset.assetId)}
                    disabled={deletingAssetId === asset.assetId}
                    aria-label={`Delete ${asset.name ?? asset.assetId}`}
                  >
                    {deletingAssetId === asset.assetId ? 'Deleting…' : 'Delete'}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
